<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePromotionsStore } from '@/stores/promotions';
import { usePlatformConfigurationStore } from '@/stores/platform_configuration';

/**
 * Renders the promotions configured on a widget.
 *
 * The backend returns the active language in its configured order, so this
 * component only presents them and routes on click.
 */
const props = defineProps({
	widget: { type: Object, required: true },
});

const router = useRouter();
const promotionsStore = usePromotionsStore();
const platformConfigurationStore = usePlatformConfigurationStore();

const promotions = computed(() => props.widget.items || []);

const goToPromotion = (promotion) => {
	if (promotion.slug === router.currentRoute.value.params.promotion_slug) return;

	promotionsStore.fetchPromotion(promotion.slug).then(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
		router.push({ name: 'promotion', params: { promotion_slug: promotion.slug } });
	});
};
</script>

<template>
	<div class="marketing-promotion-list">
		<div
			v-for="promotion in promotions"
			:key="promotion.id"
			class="marketing-promotion-card"
			@click="goToPromotion(promotion)"
		>
			<div class="marketing-promotion-image">
				<img :src="promotion.media_url" :alt="promotion.title">
			</div>
			<div v-if="platformConfigurationStore.showTextInsidePromotions" class="marketing-promotion-content">
				<h5>{{ promotion.title }}</h5>
				<p v-if="promotion.subtitle">{{ promotion.subtitle }}</p>
			</div>
		</div>
	</div>
</template>

<style scoped>
/*
 * The placement is a narrow column on some pages and the full width on others, so
 * cards per row follow the available width.
 */
.marketing-promotion-list {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
	gap: 12px;
	width: 100%;
}
.marketing-promotion-card {
	position: relative;
	cursor: pointer;
	border-radius: var(--rounded-lg, 8px);
	overflow: hidden;
	min-width: 0;
}
.marketing-promotion-image img {
	width: 100%;
	height: auto;
	display: block;
	object-fit: cover;
}
.marketing-promotion-content {
	padding: 8px 10px;
}
.marketing-promotion-content h5 {
	margin: 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--white, #fff);
}
.marketing-promotion-content p {
	margin: 2px 0 0 0;
	font-size: 11px;
	color: var(--text-secondary-color, #9a9a9a);
}

@media screen and (max-width: 768px) {
	.marketing-promotion-list {
		display: flex;
		flex-wrap: nowrap;
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
		scroll-snap-type: x mandatory;
		scrollbar-width: none;
	}
	.marketing-promotion-list::-webkit-scrollbar {
		display: none;
	}
	.marketing-promotion-card {
		flex: 0 0 calc(50% - 6px);
		min-width: calc(50% - 6px);
		max-width: calc(50% - 6px);
		scroll-snap-align: start;
	}
}
</style>
